import { notAllowedWebviewRefreshVersionCodes } from "@payment-client/constants/WebviewRefresh";
import { ShowPaymentCacheValue } from "@payment-client/interfaces/ShowPayments";
import {
    GlobalHeaderState,
    ProcessOrderActionPayload,
    SwgyHeaderKeysList,
    getBridgeSdk,
    CheckoutActionTypes,
    getOriginalBusinessLine,
    CheckoutAction,
    NEWRELIC_TABLES,
    recordNREvent,
    getUsersLastState,
    setPersistOrderDataInLocalStorage,
    SetDataSource,
    SCENES_FULL_PAYMENT_FLOW_KEY,
    SCENES_FULL_PAYMENT_ORDER_ID_KEY,
    DineoutCartType,
} from "@swiggy-private/checkout-core";
import { isString } from "@swiggy-private/js-utils";
import { BOOLEAN_AS_STRING, MARKETPLACE_BUSINESS_LINE, MARKETPLACE_CATEGORY } from "@swiggy-private/payment-types";
import { PAYMENT_INITIAL_STATE } from "@payment-client/components/payment-main/PaymentMain";
import { Storage } from "@swiggy-private/core-ui";

export const isIos = (): boolean =>
    typeof navigator !== "undefined" && (navigator.userAgent.match(/(iPad|iPhone|iPod)/g) ? true : false);

export const isAndroidPWA = (): boolean => {
    if (typeof window === "undefined" || typeof navigator === "undefined") return false;
    return /Android/i.test(navigator.userAgent) && window.matchMedia("(display-mode: standalone)").matches;
};

export const getBusinessLine = (): string => {
    return GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceBusinessLine) || MARKETPLACE_BUSINESS_LINE.REGULAR;
};

export const isBusinessLineSuper = (): boolean => {
    return getBusinessLine() === MARKETPLACE_BUSINESS_LINE.SWIGGY_SUPER;
};

export const isBusinessLineCBCC = (): boolean => {
    return getBusinessLine() === MARKETPLACE_BUSINESS_LINE.CBCC;
};

export const isBusinessLineSwiggyOne = (): boolean => {
    return getBusinessLine() === MARKETPLACE_BUSINESS_LINE.SWIGGY_ONE;
};

export const isBusinessLineDash = (): boolean => {
    return getBusinessLine() === MARKETPLACE_BUSINESS_LINE.DASH;
};

export function isBusinessLineSwiggyMoney(): boolean {
    return getBusinessLine() == MARKETPLACE_BUSINESS_LINE.SWIGGY_MONEY;
}

export const isBusinessLineDineout = (): boolean => {
    return getBusinessLine().toLowerCase() === MARKETPLACE_BUSINESS_LINE.DINEOUT;
};

export const isBusinessLineRegular = (): boolean => {
    return getBusinessLine().toLowerCase() === MARKETPLACE_BUSINESS_LINE.REGULAR;
};

export const isBusinessLinePayOnDelivery = (): boolean => {
    return getBusinessLine() === MARKETPLACE_BUSINESS_LINE.PAY_ON_DELIVERY;
};

export function isOriginalBusinessLineSwiggyMoney(): boolean {
    return getOriginalBusinessLine() === MARKETPLACE_BUSINESS_LINE.SWIGGY_MONEY;
}

export function isBusinessLineGifting(): boolean {
    return getBusinessLine()?.toLowerCase() === MARKETPLACE_BUSINESS_LINE.GIFTING;
}

export function isCategoryInstamart(): boolean {
    const headers = GlobalHeaderState.getHeaders();
    const category = headers.marketplacecategory;

    return category?.toLowerCase() === MARKETPLACE_CATEGORY.INSTAMART;
}

export function isInstamartPayOnDelivery(): boolean {
    return isBusinessLinePayOnDelivery() && isCategoryInstamart();
}

export const isPaymentModesState = (state?: PAYMENT_INITIAL_STATE): boolean =>
    state != null &&
    [
        PAYMENT_INITIAL_STATE.ADD_NEW_CARD,
        PAYMENT_INITIAL_STATE.MANAGE_CARD,
        PAYMENT_INITIAL_STATE.SECURE_CARD,
        PAYMENT_INITIAL_STATE.CONFIRM_TRANSACTION,
        PAYMENT_INITIAL_STATE.PENNY_TXN_STATUS_POLLER,
        PAYMENT_INITIAL_STATE.LINK_WALLET_WITH_NUMBER,
        PAYMENT_INITIAL_STATE.ADD_MONEY_AND_DELINK_WALLET,
        PAYMENT_INITIAL_STATE.PAYLATER_LINKING_CONFIRMATION,
    ].indexOf(state) >= 0;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getIntialStateName = (state?: PAYMENT_INITIAL_STATE, routeData?: any): { [key: string]: string } => {
    switch (state) {
        case PAYMENT_INITIAL_STATE.ADD_NEW_CARD:
        case PAYMENT_INITIAL_STATE.MANAGE_CARD:
        case PAYMENT_INITIAL_STATE.LINK_WALLET_WITH_NUMBER:
        case PAYMENT_INITIAL_STATE.PAYLATER_LINKING_CONFIRMATION:
            return { state };
        case PAYMENT_INITIAL_STATE.ADD_MONEY_AND_DELINK_WALLET:
            return { state, walletCode: routeData?.walletCode };
        case PAYMENT_INITIAL_STATE.SECURE_CARD:
        case PAYMENT_INITIAL_STATE.CONFIRM_TRANSACTION:
        case PAYMENT_INITIAL_STATE.PENNY_TXN_STATUS_POLLER:
        default:
            return { state: PAYMENT_INITIAL_STATE.SECURE_CARD };
    }
};

export const getPaymentErrorScreenAction = (
    state: PAYMENT_INITIAL_STATE,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    routeData?: any,
): {
    onClick: VoidFunction;
    text: string;
} => {
    const intialState = getIntialStateName(state, routeData);
    const queryParams = new URLSearchParams(intialState).toString();
    return {
        onClick: function (): void {
            window.location.href = window.location.origin + `?${queryParams}`;
        },
        text: "RETRY",
    };
};

export const getVersionCode = () => {
    const versionCode = GlobalHeaderState.getHeader(SwgyHeaderKeysList.versionCode);
    return Number(versionCode);
};

export const saveUsersLastStateDuringProcessOrder = async (
    processOrderData: ProcessOrderActionPayload,
    paymentCode: string,
) => {
    const versionCode = getVersionCode();
    try {
        // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
        recordNREvent({
            eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
            name: "saveUsersLastStateDuringProcessOrder",
            paymentCode: paymentCode,
            orderIdSentToApp: processOrderData.order?.orderId || "",
            paymentTxnId: processOrderData.order?.paymentTxnId || "",
        });
    } catch {
        /* No-op */
    }
    if (!isBusinessLineSwiggyMoney() && !notAllowedWebviewRefreshVersionCodes.includes(versionCode)) {
        const payload = {
            state: CheckoutActionTypes.PROCESS_ORDER,
            infoObject: {
                ...processOrderData,
                webviewRefreshExtraParams: {
                    webviewRefreshed: true,
                    redirectToPaymentPageWhenWebviewRefreshed: true,
                },
            },
            paymentCode,
        };
        try {
            setPersistOrderDataInLocalStorage(payload, SetDataSource.PROCESS_ORDER);
        } catch {
            // no-op
        }
        await getBridgeSdk()
            .saveUsersLastState(payload)
            .catch(() => void 0);
    }
};

const getOrderIdFromWebviewLastState = () => {
    const lastState = getUsersLastState();
    if (lastState) return lastState.infoObject?.order?.orderId || "";
    return "--";
};

export const clearWebviewRefreshSavedState = () => {
    // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
    try {
        recordNREvent({
            eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
            name: "clearWebviewRefreshSavedStatePre",
            preStateOrderId: getOrderIdFromWebviewLastState(),
        });
    } catch {
        /* no-op */
    }
    const versionCode = getVersionCode();
    if (!notAllowedWebviewRefreshVersionCodes.includes(versionCode)) {
        overrideWebviewStateAppJSBridgeHandler();
        getBridgeSdk()
            .saveUsersLastState({})
            .catch(() => void 0);
    }
    try {
        recordNREvent({
            eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
            name: "clearWebviewRefreshSavedStatePost",
            postStateOrderId: getOrderIdFromWebviewLastState(),
        });
    } catch {
        /* no-op */
    }
};

export const canUseShowPaymentsDataFromCache = ({
    dataInCache,
    isSwiggyMoneyFlow,
    isTurboPaymentMethod,
    turboPayUniqueId,
    cartKey,
    checkoutState,
    isBillToCompanySelected = false,
}: {
    isSwiggyMoneyFlow: boolean;
    dataInCache: ShowPaymentCacheValue;
    isTurboPaymentMethod: boolean;
    turboPayUniqueId: string;
    cartKey?: string;
    checkoutState?: CheckoutAction;
    isBillToCompanySelected?: boolean;
}) => {
    // In the case of PAYLATER_SIMPL_LINKING_FAILED and error is undefined we are rendering the Paylater group page and doing the GPO call again to fetch the latest state
    // so to do the GPO call this function should return false if it will return true GPO call will not happen again and all the value will be used from cache
    const isSimpleError =
        checkoutState?.type === CheckoutActionTypes.PAYLATER_SIMPL_LINKING_FAILED && !checkoutState?.payload?.error;
    const cantUseCache = !dataInCache.key || !isString(cartKey) || !cartKey.length || dataInCache.key == cartKey;
    const turboIdChanged = isTurboPaymentMethod && turboPayUniqueId !== dataInCache.turboPayUniqueId;
    const billToCompanySelectionChanged = isBillToCompanySelected !== dataInCache.isBillToCompanySelected;
    return !isSimpleError && !isSwiggyMoneyFlow && cantUseCache && !turboIdChanged && !billToCompanySelectionChanged;
};

export const overrideWebviewStateAppJSBridgeHandler = () => {
    if (typeof window !== "undefined" && typeof window?.__webview_state__?.getWebViewLastState === "function") {
        try {
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_JS_BRIDGE_OVERRIDE,
            });
        } catch {
            /* No-op */
        }
        window.__webview_state__.getWebViewLastState = () => {
            return "{}";
        };
    }
};

/**
 * Clears the scenes full payment session storage flags.
 * Should be called after confirm order is successful to prevent
 * affecting subsequent orders.
 */
export const clearScenesFullPaymentFlags = (): void => {
    Storage.removeItem(SCENES_FULL_PAYMENT_FLOW_KEY);
    Storage.removeItem(SCENES_FULL_PAYMENT_ORDER_ID_KEY);
};

export const isScenesFullPaymentFlow = (): boolean => {
    return Storage.getItem(SCENES_FULL_PAYMENT_FLOW_KEY) === BOOLEAN_AS_STRING.TRUE;
};

export const isStealStoreCart = (cartType?: DineoutCartType | null): boolean => {
    return cartType === DineoutCartType.STEAL_STORE_CART;
};
